
import useMobileNavigationVisibility from '@/mixins/useMobileNavigationVisibility';
import useResponsiveness from '@/mixins/useResponsiveness';
import { InfoBoxState } from '@/models/InfoBox';
import usePageState from '@/state/pageState';
import Full from '@/views/layouts/Full.vue';
import LoginRegister from '@/views/layouts/LoginRegister.vue';
import Plain from '@/views/layouts/Plain.vue';
import Landingpage from '@/views/layouts/Landingpage.vue';
import Vue, {
  computed,
  onMounted,
  provide,
  watch
} from 'vue';
import { useRoute } from 'vue2-helpers/vue-router';
import { mapMutations, MutationMethod } from 'vuex';
import { logDebug } from '@/utils/logger';
import { useIntervalFn, whenever } from '@vueuse/core';
import useTokenState from '@/state/tokenState';
import { useStore } from 'vue2-helpers/vuex';
import BCXAgbPopup from '@/components/BCXAgbPopup.vue';
import MeetingScheduler from '@/components/meeting-scheduler/pages/MeetingScheduler.vue';
import useAppVersionState from './state/appVersionState';
import useAgbState from './state/agbState';
import BCXInfoBanner from './components/molecules/BCXInfoBanner.vue';
import BCXMarkdown from './components/molecules/BCXMarkdown.vue';
import useI18n from './mixins/useI18n';
import BCXPdfPopup from './components/molecules/BCXPdfPopup.vue';
import usePopupState from './state/usePopupState';

const defaultLayout = 'plain';

export default Vue.extend({
  name: 'App',

  components: {
    Plain,
    Full,
    LoginRegister,
    Landingpage,
    BCXInfoBanner,
    BCXMarkdown,
    BCXAgbPopup,
    BCXPdfPopup,
    MeetingScheduler
  },

  setup() {
    const { isHidden } = useMobileNavigationVisibility();
    const { isSidebarVisible, isDesktopWidth } = useResponsiveness();
    const { mainClasses, pageNotFound, globalErrorMessage } = usePageState();
    const { t, locale } = useI18n();
    const { isLoggedIn } = useTokenState();
    const store = useStore();
    const { popupState } = usePopupState();

    whenever(isLoggedIn, () => {
      const { user } = store.getters;
      const prefLocale = user.preferredLocale?.substring?.(0, 2);
      if (prefLocale) locale.value = prefLocale;
    }, { immediate: true });

    const route = useRoute();

    const {
      isAppUpdateAvaliable, isNotificationIgnored, ignoreNotification, updateApp
    } = useAppVersionState();

    const showAppUpdateNotification = computed(() => (
      isAppUpdateAvaliable.value
      || pageNotFound.value
      || globalErrorMessage.value)
      && !isNotificationIgnored.value);

    watch(showAppUpdateNotification, (is) => {
      if (is) {
        logDebug('update notification shown');
      } else {
        pageNotFound.value = false;
      }
    });

    const notificationCloseHandler = (callback: () => void) => {
      if (pageNotFound.value) {
        pageNotFound.value = false;
        globalErrorMessage.value = null;
        return;
      }

      if (globalErrorMessage.value?.withoutReload) {
        globalErrorMessage.value = null;
        return;
      }

      globalErrorMessage.value = null;
      callback();
    };

    const closeNotification = () => {
      notificationCloseHandler(ignoreNotification);
    };
    const reloadAppIfNeeded = () => {
      notificationCloseHandler(updateApp);
    };

    // #region Agb Popup Interval
    // Every AGB_POPUP_INTERVAL_LENGTH, we check if the user has not yet accepted the latest AGB version
    // If they have not, and they are past the grace period, we show the AGB popup
    // We also check it immediately on app load, otherwise users can skip the popup by refreshing the page

    const { isAgbPopupVisible, shouldShowAgbPopup, showAgbPopup } = useAgbState();

    const checkAgbPopup = async () => {
      if (!isLoggedIn.value || isAgbPopupVisible.value) return;

      const shouldShowAgbPopupResult = await shouldShowAgbPopup();
      if (shouldShowAgbPopupResult) {
        await showAgbPopup();
      }
    };

    checkAgbPopup();

    const AGB_POPUP_INTERVAL_LENGTH = 15 * 60 * 1000; // 15 minutes

    onMounted(() => {
      useIntervalFn(checkAgbPopup, AGB_POPUP_INTERVAL_LENGTH);
    });

    // #endregion

    provide('isMobileLayerHidden', isHidden);

    const layout = computed(() => route.meta?.layout ?? defaultLayout);

    const classes = computed(() => [layout.value, ...mainClasses.value]);

    const appUpdateText = computed(() => {
      let text: string;
      if (globalErrorMessage.value) {
        const { code, i18Key, withoutReload } = globalErrorMessage.value;
        if (withoutReload) {
          const message = `${t(i18Key, { code })}`;
          text = `${t('common.globalError', { message })}`;
        } else {
          const message = `${t(i18Key, { code })}`;
          text = `${t('common.globalErrorReload', { message })}`;
        }
      } else if (pageNotFound.value) {
        text = `${t('common.pageNotFound')}`;
      } else {
        text = `${t('common.appUpdateAvailable')}`;
      }
      if (isDesktopWidth.value) {
        return text.replace('<br>', '');
      }
      return text;
    });

    const infoBannerType = computed(() => {
      if (pageNotFound.value || globalErrorMessage.value) return 'page-not-found';
      return 'app-update';
    });

    return {
      isSidebarVisible,
      classes,
      layout,
      route,
      showAppUpdateNotification,
      closeNotification,
      reloadAppIfNeeded,
      isDesktopWidth,
      appUpdateText,
      pageNotFound,
      infoBannerType,
      isAgbPopupVisible,
      popupState
    };
  },

  data: () => ({
    showErrorMessage: false,
    errorMessageTimeout: -1,
    sidebarBox: null
  }),

  computed: {
    errorMessage(): string {
      return this.$store.getters.errorMessage;
    },
    sidebarComponent(): Vue | null {
      return this.$route.meta?.sidebarComponent ?? null;
    },
    infoBoxContent(): InfoBoxState | null {
      return this.$route.meta?.infoBox ?? null;
    }
  },

  created() {
    const config: { [key: string]: MutationMethod } = {
      '(max-width:480px)': this.setIsSmartphoneWidth,
      '(max-width:767px)': this.setIsMobileWidth,
      '(min-width:768px) and (max-width:1439px)': this.setIsMini,
      '(max-height:640px)': this.setIsFlat,
      '(min-width:768px) and (max-width:1255px)': this.setIsTabletWidth,
      '(min-width:1256px)': this.setIsDesktopWidth
    };
    Object.entries(config).forEach(([mediaQuery, setter]) => {
      const mql: MediaQueryList = window.matchMedia(mediaQuery);
      setter(mql.matches);
      mql.onchange = (evt) => setter(evt.matches);
    });
  },

  methods: {
    ...mapMutations('page', ['setIsFlat', 'setIsMini', 'setIsMobileWidth', 'setIsTabletWidth', 'setIsDesktopWidth', 'setIsSmartphoneWidth'])
  }
});
